import { FiUser } from '@react-icons/all-files/fi/FiUser';
import './styles.scss';
import { Auth } from 'aws-amplify';
import cx from 'classnames';
import { Field, Form, Formik } from 'formik';
// biome-ignore lint/style/useImportType: <explanation>
import React, { useCallback, useMemo, useState } from 'react';
import { Nav, NavDropdown, Navbar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Modal, Row, Spinner } from 'reactstrap';
import * as Yup from 'yup';
import { clearAccount } from '~redux/account/actions/account';
import { accountSelector } from '~redux/account/selectors/account';
import { clearAuthUser } from '~redux/auth/actions/authUser';
import { clearBuyerInfo } from '~redux/buyer/actions/buyerInfo';

type AgreementModalProps = {
  isShow: boolean;
  toggle: () => void;
};

type AgreementFormValues = Yup.InferType<typeof agreementFormSchema>;

const agreementFormSchema = Yup.object().shape({
  agreement: Yup.boolean()
    .oneOf([true], '利用規約に同意してください。')
    .required('利用規約に同意してください。'),
});

export const AgreementModal: React.FC<AgreementModalProps> = ({
  isShow,
  toggle,
}) => {
  const dispatch = useDispatch();

  // User Menu
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const showUserMenu = useCallback(() => {
    setUserMenuOpen(true);
  }, []);
  const hideUserMenu = useCallback(() => {
    setUserMenuOpen(false);
  }, []);
  const signOut = useCallback(async () => {
    await Auth.signOut();
    dispatch(clearAuthUser());
    dispatch(clearAccount());
    dispatch(clearBuyerInfo());
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  }, []);

  // form
  const account = useSelector(accountSelector);
  const initialValues = useMemo(() => {
    return {
      agreement: false,
    } as AgreementFormValues;
  }, [account]);

  const onSubmit = useCallback(
    async (values: AgreementFormValues) => {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        // biome-ignore lint/style/noNonNullAssertion: <explanation>
        'custom:agreement': String(values.agreement!),
      });
      toggle();
    },
    [account, toggle]
  );

  return (
    <>
      {isShow && (
        <Navbar className="agreement-modal-navbar">
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav>
              <NavDropdown
                id="user-menu"
                className="menu-dropdown-account"
                title={<FiUser size={20} />}
                show={userMenuOpen}
                onMouseEnter={() => showUserMenu()}
                onMouseLeave={() => hideUserMenu()}
                onTouchStart={() => showUserMenu()}
                alignRight
              >
                <Nav.Item className="user-menu_user-info">
                  {account?.last_name && account?.first_name
                    ? `${account?.last_name}　${account?.first_name} 様`
                    : ''}
                </Nav.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item onClick={signOut}>
                  ログアウト
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Navbar>
      )}
      <Modal
        isOpen={isShow}
        contentClassName="agreement-modal"
        backdropClassName="agreement-modal-overlay"
      >
        <h2 className="text-center">同意フォーム</h2>
        <p>
          このたび、弊社はさらなるサービス拡充および事業成長を目的とし、2025年4月1日付にて本マーケットプレイス事業をorosy株式会社へ譲渡することになりました。
        </p>
        <ul className="mb-6">
          <li>
            <strong>アカウント情報・契約条件</strong>
            ご登録のアカウント情報（ID・パスワード等）や契約条件等は、原則として新運営会社へ引き継がれる予定です。ただし、サービス利用規約や個人情報の取り扱い等に変更が生じる可能性があるため、
            <strong>
              改めて本件にご同意をいただいたユーザー様から順次、移行を進めさせていただきます。
            </strong>
          </li>
          <li>
            2025年4月1日より、新運営会社策定の新規約が適用される予定です。事業譲渡に同意いただける場合は、合わせてリンク先の新運営会社による規約の変更内容についてもご確認いただき、かかる規約変更についてもご確認いただいた上でご同意をお願いいたします。なお、事業譲渡にご同意いただいた場合、こちらについてもご同意いただいたものとみなさせていただきます。
            <br />
            URL：
            <a
              href="https://docs.google.com/document/d/1AcVbAqtrJLhfWc8arIkhKMzUHLhaN0nN"
              target="_blank"
              rel="noopener noreferrer"
              style={{ wordBreak: 'break-all' }}
            >
              https://docs.google.com/document/d/1AcVbAqtrJLhfWc8arIkhKMzUHLhaN0nN
            </a>
          </li>
          <li>
            新運営会社のプライバシーポリシーは以下のURLからご確認いただけます。事業譲渡に同意いただける場合は、合わせてリンク先の新運営会社のプライバシーポリシーについてもご確認いただいた上でご同意をお願い致します。なお、事業譲渡にご同意いただいた場合、こちらについてもご同意いただいたものとみなさせていただきます。
            <br />
            URL：
            <a
              href="https://privacy.spaceengine.io/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ wordBreak: 'break-all' }}
            >
              https://privacy.spaceengine.io/
            </a>
          </li>
        </ul>
        <Formik
          validationSchema={agreementFormSchema}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formik) => (
            <Form className="agreement-form">
              <div className="mb-5">
                <Row>
                  <Col>
                    <div className="checkbox">
                      <Field
                        id="agreement"
                        type="checkbox"
                        name="agreement"
                        className="form-control is-invalid"
                      />
                      <label htmlFor="agreement">
                        orosy利用規約及びプライバシーポリシーへの同意
                        <span className="required-label">*</span>
                      </label>
                    </div>
                    {formik.errors.agreement && (
                      <div className="invalid-feedback">
                        {formik.errors.agreement}
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
              <div className="d-flex border-top pt-4">
                <Button
                  className="ml-auto"
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting && (
                    <Spinner size="sm" className="mr-2" />
                  )}
                  送信
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};
